import type { ComponentType } from 'react'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import type { IndustryData, IndustryLayout } from '@/data/industries'
import { BlogsListingLayout } from './layouts/BlogsListingLayout'
import { DockLayout } from './layouts/DockLayout'
import { GiantWordLayout } from './layouts/GiantWordLayout'
import { StatementLayout } from './layouts/StatementLayout'
import { StatsHeroLayout } from './layouts/StatsHeroLayout'
import { TornadoLayout } from './layouts/TornadoLayout'

interface IndustryPageProps {
  industry: IndustryData
}

/**
 * One layout per industry page, so neighbours in the mega menu never share
 * a composition:
 * - blogs-listing (Legal): document-fan hero, filterable listing, pain points, roles, badges.
 * - stats-hero (Finance): purple stats hero, pinned workflow diagrams, template grid, before/after.
 * - statement (Healthcare): statement hero, clinical form media, roles grid, workflow timeline.
 * - card-tornado (Insurance): split hero with a document tornado, rotated cards, agent dock.
 * - giant-word (Engineering): giant word over a document register, workflow rows, numbers band.
 * - dock-marquee (Consulting): pink-box hero with a deliverables dock, pink stats, scroll cards.
 */
const LAYOUTS: Record<IndustryLayout, ComponentType<IndustryPageProps>> = {
  'blogs-listing': BlogsListingLayout,
  'stats-hero': StatsHeroLayout,
  statement: StatementLayout,
  'card-tornado': TornadoLayout,
  'giant-word': GiantWordLayout,
  'dock-marquee': DockLayout,
}

export function IndustryPage({ industry }: IndustryPageProps) {
  const Layout = LAYOUTS[industry.layout] ?? BlogsListingLayout

  return (
    <>
      <PageMeta title={industry.metaTitle} description={industry.metaDescription} />

      <LandingLayout>
        <div data-layout={industry.layout}>
          <Layout industry={industry} />
        </div>
      </LandingLayout>
    </>
  )
}
